import React, { useContext, useEffect, useMemo, useState } from 'react';
import { decode } from 'base-64';
import { Outlet } from 'react-router-dom';
import { SHLContext, ShlLinkContext } from '../Contexts';
import PINEntryPage from '../pages/PINEntryPage';
import { isSHLExpired } from '../utils/shlUtil';
import { ErrorTypes } from '../constants';
import { GenericError, VSError } from '../model/error';

/**
 * Users must complete PIN authentication flow to see children of this route.
 * @returns {React.FunctionComponent} React element that enforces authentication.
 */
function ProtectedRoute() {
  const [files, setFiles] = useState([]);
  const [encryptionKey, setEncryptionKey] = useState('');
  const [isExpiryChecked, setIsExpiryChecked] = useState(false);
  const shl = useContext(ShlLinkContext);

  useEffect(() => {
    // Check SHL expiry and throw error if expired
    if (shl && isSHLExpired(shl)) {
      throw new VSError(ErrorTypes.EXPIRED, '', '', 'FHIR_EXP', 'FHIR');
    }
    setIsExpiryChecked(true);
  }, []);

  const memoizedValue = useMemo(
    () => ({
      files,
      setFiles,
      encryptionKey,
      setEncryptionKey,
    }),
    [files, encryptionKey],
  );

  if (!shl) {
    throw new GenericError(ErrorTypes.PAGE_NOT_FOUND);
  }

  // Look at SHL payload to see if PIN is required
  const parsedJSON = JSON.parse(decode(shl));
  const requiresPIN = parsedJSON.flag.includes('P');

  // If PIN required, then don't show PS home page until required secure files are obtained
  const hasAuthorization = !requiresPIN || (files?.length && encryptionKey);

  return (
    isExpiryChecked && (
      <SHLContext.Provider value={memoizedValue}>
        {hasAuthorization ? <Outlet /> : <PINEntryPage shl={parsedJSON} />}
      </SHLContext.Provider>
    )
  );
}

export default ProtectedRoute;
